import { useEffect, useState, type FormEvent } from 'react';
import {
  MAIL_FUNCTIONS,
  MAIL_FUNCTION_LABELS,
  type MailFunction,
  type MailSettings,
} from '@catalyst-psa/shared';

import { useAuth } from '../../auth/authContext.js';
import { PageHeader } from '../../components/layout/PageHeader.js';
import { Badge } from '../../components/ui/Badge.js';
import { Button } from '../../components/ui/Button.js';
import { DataState } from '../../components/ui/DataState.js';
import { TextField } from '../../components/ui/Field.js';
import { useMailSettings, useUpdateMailSettings } from './api.js';

type SenderState = Record<MailFunction, string>;

function emptySenders(): SenderState {
  return MAIL_FUNCTIONS.reduce((acc, fn) => {
    acc[fn] = '';
    return acc;
  }, {} as SenderState);
}

function toSenderState(settings: MailSettings | undefined): SenderState {
  const state = emptySenders();
  if (settings) {
    for (const fn of MAIL_FUNCTIONS) state[fn] = settings.senders[fn] ?? '';
  }
  return state;
}

export function MailSettingsPage(): JSX.Element {
  const { isAdmin } = useAuth();
  const query = useMailSettings();
  const settings = query.data;
  const updateMutation = useUpdateMailSettings();

  const [defaultSender, setDefaultSender] = useState('');
  const [senders, setSenders] = useState<SenderState>(emptySenders);
  const [readyToBillNotifyEmails, setReadyToBillNotifyEmails] = useState('');
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (settings) {
      setDefaultSender(settings.defaultSender ?? '');
      setSenders(toSenderState(settings));
      setReadyToBillNotifyEmails(settings.readyToBillNotifyEmails ?? '');
    }
  }, [settings]);

  const handleSubmit = (event: FormEvent): void => {
    event.preventDefault();
    setSaved(false);
    updateMutation.mutate(
      {
        defaultSender: defaultSender.trim() ? defaultSender.trim() : null,
        senders: MAIL_FUNCTIONS.reduce(
          (acc, fn) => {
            acc[fn] = senders[fn].trim() ? senders[fn].trim() : null;
            return acc;
          },
          {} as Record<MailFunction, string | null>,
        ),
        readyToBillNotifyEmails: readyToBillNotifyEmails.trim()
          ? readyToBillNotifyEmails.trim()
          : null,
      },
      { onSuccess: () => setSaved(true) },
    );
  };

  if (!isAdmin) {
    return (
      <div>
        <PageHeader title="Email Settings" />
        <p className="text-sm text-slate-500">You do not have access to email settings.</p>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="Email Settings"
        description="Configure the From mailbox PSA sends each kind of email as. The default applies unless a function has its own sender."
      />

      <DataState
        isLoading={query.isLoading}
        isError={query.isError}
        error={query.error}
        isEmpty={false}
        onRetry={() => void query.refetch()}
      >
        {settings ? (
          <div className="max-w-2xl space-y-6">
            <div className="flex flex-wrap items-center gap-2">
              {settings.hasCredentials ? (
                <Badge tone="green">Graph credentials present</Badge>
              ) : (
                <Badge tone="red">Graph credentials missing</Badge>
              )}
              {settings.configured ? (
                <Badge tone="green">Email enabled</Badge>
              ) : (
                <Badge tone="amber">Email not yet sendable</Badge>
              )}
              {settings.envSender ? (
                <Badge tone="gray">{`Env fallback: ${settings.envSender}`}</Badge>
              ) : null}
            </div>

            {!settings.hasCredentials ? (
              <p className="rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-700">
                The Microsoft Graph credentials (GRAPH_TENANT_ID, GRAPH_CLIENT_ID,
                GRAPH_CLIENT_SECRET) are not set on the server. Senders can be configured here,
                but no email will send until the credentials are provisioned.
              </p>
            ) : null}

            <form onSubmit={handleSubmit} className="space-y-5">
              <TextField
                label="Default sender mailbox"
                type="email"
                placeholder="invoices@nscatalyst.com"
                value={defaultSender}
                onChange={(e) => setDefaultSender(e.target.value)}
              />

              <div className="space-y-4 rounded-lg border border-slate-200 p-4">
                <p className="text-sm font-medium text-slate-700">Per-function senders</p>
                <p className="text-xs text-slate-500">
                  Leave a field blank to use the default sender above.
                </p>
                {MAIL_FUNCTIONS.map((fn) => (
                  <TextField
                    key={fn}
                    label={MAIL_FUNCTION_LABELS[fn]}
                    type="email"
                    placeholder={defaultSender.trim() || 'Use default sender'}
                    value={senders[fn]}
                    onChange={(e) =>
                      setSenders((prev) => ({ ...prev, [fn]: e.target.value }))
                    }
                  />
                ))}
              </div>

              <div className="space-y-2 rounded-lg border border-slate-200 p-4">
                <p className="text-sm font-medium text-slate-700">Internal notifications</p>
                <TextField
                  label="Ready to Bill notification email"
                  placeholder="billing@nscatalyst.com, pmo@nscatalyst.com"
                  value={readyToBillNotifyEmails}
                  onChange={(e) => setReadyToBillNotifyEmails(e.target.value)}
                />
                <p className="text-xs text-slate-500">
                  Emailed whenever a project is closed with &quot;Close project &amp; mark Ready
                  to Bill&quot; (project form or public project link). Separate multiple
                  addresses with commas; leave blank to disable.
                </p>
              </div>

              {updateMutation.isError ? (
                <p className="rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-700">
                  Failed to save email settings.
                </p>
              ) : null}

              <div className="flex items-center gap-3">
                <Button
                  type="submit"
                  variant="primary"
                  isLoading={updateMutation.isPending}
                  disabled={updateMutation.isPending}
                >
                  Save settings
                </Button>
                {saved && !updateMutation.isPending ? (
                  <span className="text-sm text-emerald-600">Saved</span>
                ) : null}
              </div>
            </form>
          </div>
        ) : null}
      </DataState>
    </div>
  );
}
